<script setup lang="ts">
    import { computed, defineEmits, defineProps, onMounted, ref, toRefs, watch, withDefaults } from "vue";

    type Option = {
        label: string;
        value: string | null;
        icon?: string;
        disabled: boolean;
        isTitle?: boolean;
    };

    const props = withDefaults(
        defineProps<{
            options: Array<Option>;
            optionValue?: string;
            optionLabel?: string;
            placeholder?: string;
            icons: {
                [key: string]: string;
            };
            initialValue?: string;
            dropdownTitle?: string;
            filterable?: boolean;
            editable?: boolean | ((initialValue?: string) => boolean);
            // Om true: uppdatera inte UI-valet direkt, utan lat parent styra via initialValue.
            deferSelection?: boolean;
        }>(),
        {
            filterable: true,
            editable: false,
            optionValue: "value",
            optionLabel: "label",
            deferSelection: false,
        }
    );

    const isEditable = computed(() => {
        if (typeof props.editable === "function") {
            return props.editable(props.initialValue);
        }

        return props.editable;
    });

    const { options, optionLabel, optionValue, placeholder, dropdownTitle, initialValue, deferSelection } =
        toRefs(props);

    const emit = defineEmits(["update:modelValue", "statusChanged"]);

    const selectedValue = ref<string | null>(null);
    const dropdown = ref(null);
    const internalInitialValue = ref();
    const modifiedOptions = ref<Option[]>([]);
    const emitStatusValue = ref(selectedValue.value);

    const updateModifiedOptions = () => {
        if (dropdownTitle?.value) {
            modifiedOptions.value = [
                {
                    label: dropdownTitle.value,
                    value: null,
                    isTitle: true,
                    disabled: true,
                },
                ...options.value,
            ];
        } else {
            modifiedOptions.value = options.value;
        }
    };

    onMounted(() => {
        updateModifiedOptions();
        emitStatusValue.value = selectedValue.value;
    });

    watch([options, dropdownTitle], () => {
        updateModifiedOptions();
    });
    const onDropdownItemSelect = (event: { value: string; originalEvent: Event }) => {
        const isChanged = event.value !== internalInitialValue.value;

        // Hitter inte bra lösning för att inte tillåta användaren skriva eller välja upp eller ner knapper via tangentbordet.
        if ((event.originalEvent && event.originalEvent.type === "keydown") || event.originalEvent.type === "keydown") {
            selectedValue.value = initialValue?.value ?? null;
        } else if (event.originalEvent && event.originalEvent.type === "click") {
            if (deferSelection.value) {
                // Behall tidigare vald status tills backend bekräftar.
                emitStatusValue.value = internalInitialValue.value ?? null;
                selectedValue.value = internalInitialValue.value ?? null;
            } else {
                selectedValue.value = event.value;
            }

            emit("statusChanged", {
                isChanged,
                newValue: event.value,
                initialValue: internalInitialValue.value,
            });
        } else {
            selectedValue.value = event.value;
        }
    };

    const onDropdownHide = () => {
        const isChanged = selectedValue.value !== emitStatusValue.value;

        if (isChanged) {
            emitStatusValue.value = selectedValue.value;
        }
    };

    // Det finns en timing issue och därför måste vi vänta tills renderingen är klar innan vi sätter värdet.
    // Haller dropdownens visade varde i synk med parentens initialValue.
    watch(
        // Fulhack för att få rätt typning. Oklart varför den vill att den ska vara av typen object
        initialValue as object,
        (newVal) => {
            selectedValue.value = newVal as unknown as string;
            internalInitialValue.value = newVal;
            emitStatusValue.value = (newVal as unknown as string) ?? null;
        },
        { immediate: true }
    );
</script>

<template>
    <div class="dropdown-container">
        <!-- Visar Dropdown när man har rätt att ändra status -->
        <template v-if="isEditable">
            <PrimeVueDropdown
                ref="dropdown"
                v-model="emitStatusValue"
                :options="modifiedOptions"
                :optionLabel="optionLabel"
                :optionValue="optionValue"
                :placeholder="placeholder"
                :filter="filterable"
                @change="onDropdownItemSelect"
                @hide="onDropdownHide"
                optionDisabled="disabled"
                appendTo="body"
            >
                <template #panelHeader v-if="dropdownTitle">
                    <div class="dropdown-header">
                        <h4>{{ dropdownTitle }}</h4>
                    </div>
                </template>

                <!-- Option Template -->
                <template #option="slotProps">
                    <div v-if="slotProps.option.isTitle" class="dropdown-title-option">
                        <strong>{{ slotProps.option.label }}</strong>
                    </div>
                    <div v-else class="dropdown-option">
                        <img
                            v-if="selectedValue === slotProps.option[optionValue]"
                            src="@/assets/check-dark.svg"
                            alt=""
                            width="20"
                            height="20"
                            class="checkbox-image"
                        />
                        <div v-else class="checkbox-image-empty"></div>
                        <img
                            v-if="slotProps.option.icon"
                            :src="slotProps.option.icon"
                            alt=""
                            width="20"
                            height="20"
                            class="option-icon"
                        />
                        <span class="option-label">{{ slotProps.option[optionLabel] }}</span>
                    </div>
                </template>

                <!-- Selected Value Template -->
                <!-- Användar selectedValue istället för slotProps.value pga reaktivitet,
                  det kanske fungerar bättre i vue3 men just nu måste vi använda
                  selectedValue för att kunna lösa reaktivitet problem.
                  Hittat buggen när man filtrerar lista så ser man inte aktuella värden -->
                <template #value>
                    <div v-if="selectedValue" class="selected-value">
                        <img
                            v-if="icons[selectedValue]"
                            :src="icons[selectedValue]"
                            alt=""
                            width="20"
                            height="20"
                            class="value-icon"
                        />
                        <span>{{ selectedValue }}</span>
                    </div>
                    <span v-else>{{ placeholder }}</span>
                </template>
            </PrimeVueDropdown>
        </template>
        <!-- Visar image med status om editable är false -->
        <template v-else>
            <div class="not-dropdown">
                <img
                    v-if="selectedValue && icons[selectedValue]"
                    :src="icons[selectedValue]"
                    alt="Status"
                    width="20"
                    height="20"
                    class="value-icon"
                />
                <span>{{ selectedValue }}</span>
            </div>
        </template>
    </div>
</template>
<style scoped lang="scss">
    .not-dropdown {
        display: flex;
        align-items: center;
        gap: 4px;
        margin-left: calc(12px + 16px);

        span {
        }
    }

    ::v-deep > * {
        font-family: Roboto, sans-serif;
        font-size: 14px;
        line-height: 20px;

        .p-inputtext {
            padding: 0;
        }
    }

    ::v-deep .p-dropdown {
        flex-direction: row-reverse;
        border: none;
        background-color: transparent;
        gap: 12px;

        .p-dropdown-label {
            white-space: normal;
        }

        .selected-value {
            display: flex;
            gap: 4px;
            justify-items: center;
        }

        &:not(.p-disabled).p-focus {
            border: none;
            box-shadow: none;
            border-color: red;
        }

        .p-dropdown-trigger {
            width: fit-content;
        }

        .value-icon {
            place-self: center;
        }
    }

    .p-dropdown-panel {
        border: 10px solid #999c9e; // funkar inte att sätta border-radius på dropdownen.

        .dropdown-title-option {
            padding: 8px 12px;
            border-bottom: 1px solid #ddd;
            pointer-events: none;

            &li {
                cursor: not-allowed;
                background-color: red;
            }
        }

        li {
            .dropdown-option {
                display: flex;
                align-items: center;
            }

            img {
                margin-right: 4px;
            }
        }

        .p-dropdown-items {
            .p-dropdown-item > * {
                padding: 0.5rem 8px;

                .checkbox-image {
                    width: 20px;
                    height: 20px;
                }
                .checkbox-image-empty {
                    width: 20px;
                    height: 20px;
                    margin-right: 5px;
                }
            }
        }
    }
</style>

<!-- Tvungen att visa saker ska ovverrida när vi inte scopat styling-->
<style lang="scss">
    .p-dropdown-panel {
        border: 1px solid #999c9e !important;

        .p-dropdown-items-wrapper {
            max-height: 400px !important;
        }

        .p-dropdown-items {
            .p-dropdown-item:hover {
                background-color: #cee9f3 !important;
            }

            li:has(.dropdown-title-option) {
                opacity: 1 !important;
                background-color: #fff;
            }
        }
    }
</style>
